import { useRef, useState, useSyncExternalStore } from 'react';
import { X } from '../../icons';
import { createTaskGroup, type TaskGroupDto } from '../../api/task-extras';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import './MyTasksBoard.css';

interface TaskGroupCreateModalProps {
  onClose: () => void;
  onCreated: (group: TaskGroupDto) => void;
}

/**
 * 그룹 구분용 팔레트 (AW-004-02) — **상태·위험도 색과 역할이 겹치지 않게** 따로 관리한다.
 * 상태색(완료=초록 / 진행중=파랑 / 지연·마감임박=주황 / 위험=빨강)과 같은 뜻으로 읽히면
 * 그룹 색만 보고 "이 그룹이 위험한가?" 로 오해하게 된다. 그래서 여기는 채도를 낮춘
 * 카테고리 색 12종이고, 경고를 뜻하는 순빨강은 넣지 않는다.
 */
export const GROUP_COLORS = [
  '#6366f1', '#3b82f6', '#0ea5e9', '#14b8a6', '#8b5cf6', '#a855f7',
  '#ec4899', '#f472b6', '#f59e0b', '#f97316', '#64748b', '#10b981',
];

/** AW-004-02 — "＋ 그룹 추가" 가 여는 등록 모달. 이름 + 색상만 받는다. */
export function TaskGroupCreateModal({ onClose, onCreated }: TaskGroupCreateModalProps) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  const [name, setName] = useState('');
  const [color, setColor] = useState(GROUP_COLORS[3]!);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);

  useEscapeClose(onClose, submitting);

  async function submit() {
    const n = name.trim();
    /*
     * ⚠ `TeamCreateInviteModal` 은 ★ 아직 ⭐ «우리 트리에 없다»(2026-10-10 실측) —
     *   ⇒ 정본 `origin/feature/dev-syk` 에 있고 ⇒ ★ 들이면 ⭐ «팀 만들기 흐름»이 바뀌므로
     *     ⇒ ⇒ ★ 사용자 결정이 ⭐ 필요하다(판 F-2 로 미뤘다).
     * ⇒ ⭐ 그러니 ★ 아래 규칙의 «본»은 ⇒ ⭐ 그 파일이 아니라 ★ ⭐ «이 자리와 TaskGroupCreateModal» 이다.
     *   ⇒ ⭐ 규칙 자체 ⇒ ★ 「⭐ 비활성 버튼 대신 ⇒ ★ 까닭을 말한다」.
     * ⏳ 만료 조건 ⇒ ★ 그 파일이 들어오면 ⇒ ⭐ 이 글을 지우고 ★ 그 이름을 ⭐ 되살려라.
     * ⛔ 2026-10-10 정정 — ★ 위 두 줄이 ⭐ 두 군데 ★ 좁다:
     *   ⓐ 「우리 트리에 없다」 ⇒ ★ ⭐ «일부»만 맞다 — ⇒ `TeamCreateInviteModal.css` 는
     *      ⇒ ⇒ ★ wc -l 241 줄이 ⭐ «이미 있다»(판 F-1) ⇒ ⇒ `TeamInvitePickerModal.tsx:16` 이
     *         ⇒ ⇒ ★ 그것을 ⭐ import 한다. ⇒ ⇒ ⛔ 없는 것은 ★ ⭐ «.tsx» 쪽뿐이다.
     *      ⇒ ⛔ 2026-10-10 ★ 두 번째 정정 — ⇒ ⭐ 그 .tsx 도 ★ 이제 «있다»
     *         ⇒ ★ 「정본 프로젝트 메뉴를 ⭐ 그대로 이관」의 ★ 첫 판에서 ⇒ 정본 글자로 들였다.
     *         ⇒ ⇒ ⚠ 다만 ★ «그리는 곳»은 ⭐ 아직 0 이다.
     *         ⇒ ⇒ ⇒ ★ 자세한 것은 ⭐ `ProjectPage/TeamsDetailPage.tsx` 의 ★ 「판 F-2」 쪽지.
     *   ⓑ ⏳ 「그 파일이 들어오면」 ⇒ ★ 그것은 ⭐ «파일 이름» 꼴이다 — ⇒ ⛔ 위 ⓐ 처럼 깨진다
     *      ⇒ ⇒ ★ 상태가 ⭐ «없다 / 일부 / 전부» ★ 셋이기 때문이다.
     *      ⇒ ⇒ ✅ 바른 꼴 ⇒ ⭐ 「`TeamCreateInviteModal` «이라는 이름»을 내놓는 모듈이 생기고
     *         ⇒ ⇒ ★ `TeamsDetailPage` 가 ⭐ 그것을 «그리는» 날」.
     *   ⇒ ⭐ 막는 것 ★ «전부»와 그 까닭은 ⇒ ★ `TeamsDetailPage.tsx` 의 ⭐ 「판 F-2」 쪽지에 있다.
     */
    // 비활성 버튼 대신 이유를 말한다.
    if (!n) {
      setError(tProject('project.group.err_name'));
      nameRef.current?.focus();
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      onCreated(await createTaskGroup({ name: n, color }));
    } catch (err) {
      const code = (err as any)?.response?.data?.code ?? '';
      const msg = (err as any)?.response?.data?.message ?? (err as Error).message;
      setError(`${code ? `[${code}] ` : ''}${msg ?? tProject('project.group.err_save')}`);
      setSubmitting(false);
    }
  }

  return (
    <div
      className="pj-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={tProject('project.group.title')}
    >
      <div ref={trapRef} className="pj-modal mtg-modal">
        <header className="mtg-head">
          <h2>{tProject('project.group.title')}</h2>
          <button type="button" className="mtg-close" onClick={onClose} disabled={submitting}>
            <X size={14} aria-hidden />
            {tProject('project.btn.close')}
          </button>
        </header>

        <label className="mtg-label" htmlFor="mtg-name">{tProject('project.group.name_label')}</label>
        <input
          id="mtg-name"
          ref={nameRef}
          className="mtg-input"
          aria-invalid={error != null && !name.trim()}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={tProject('project.group.name_ph')}
          maxLength={60}
          autoFocus
          disabled={submitting}
          onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }}
        />

        <div className="mtg-label mtg-label--mt">{tProject('project.group.color_label')}</div>
        <p className="mtg-colorhint">{tProject('project.group.color_hint')}</p>
        <div className="mtg-colors" role="radiogroup" aria-label={tProject('project.group.color_label')}>
          {GROUP_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={c === color}
              aria-label={c}
              className={`mtg-color${c === color ? ' mtg-color--on' : ''}`}
              style={{ background: c }}
              onClick={() => setColor(c)}
            />
          ))}
        </div>

        {error && <div className="pj-modal-error" role="alert">{error}</div>}

        <footer className="mtg-foot">
          <button type="button" className="mtg-submit" onClick={() => void submit()} disabled={submitting}>
            {submitting ? tProject('project.btn.saving') : tProject('project.group.submit')}
          </button>
          <button type="button" className="mtg-cancel" onClick={onClose} disabled={submitting}>
            {tProject('project.btn.cancel')}
          </button>
        </footer>
      </div>
    </div>
  );
}
